/**
 * Tiny URL Utilities
 *
 * Provides functions to create short-lived tiny URLs using Amazon's tiny.amazon.com service
 * for sharing filter URLs with a configurable TTL (default: 3 days)
 *
 * Note: tiny.amazon.com only supports deployed environments, not localhost.
 * Tiny URL creation will fail gracefully in local development.
 */

const TINY_URL_API = 'https://tiny.amazon.com/submit/url';
const DEFAULT_TTL_DAYS = 3; // Short-lived URLs

/**
 * Check if tiny URL creation is supported in the current environment
 * Tiny URLs work in all environments except localhost (not supported by tiny.amazon.com)
 */
export function isTinyUrlSupported(): boolean {
  const hostname = window.location.hostname;
  // Exclude localhost and localhost with domain variations (localhost.a2z.com, etc.)
  return hostname !== 'localhost' && !hostname.startsWith('localhost.');
}

export interface TinyUrlOptions {
  ttlDays?: number; // Time-to-live in days (default: 3)
}

export interface TinyUrlResponse {
  tinyUrl: string;
  fullUrl: string;
  expiresAt: Date;
}

/**
 * Create a tiny URL using Amazon's tiny.amazon.com service
 *
 * @param fullUrl - The full URL to shorten
 * @param options - Configuration options (TTL, etc.)
 * @returns Promise with tiny URL details
 * @throws Error if tiny URL creation fails or environment is not supported
 */
export async function createTinyUrl(
  fullUrl: string,
  options: TinyUrlOptions = {},
): Promise<TinyUrlResponse> {
  const { ttlDays = DEFAULT_TTL_DAYS } = options;

  // Check if environment is supported
  if (!isTinyUrlSupported()) {
    throw new Error('Tiny URL creation is not supported in this environment');
  }

  try {
    // Calculate expiration timestamp (TTL in seconds)
    const ttlSeconds = ttlDays * 24 * 60 * 60;
    const expiresAt = new Date(Date.now() + ttlSeconds * 1000);

    // Build URL with query parameters (GET request)
    const url = new URL(TINY_URL_API);
    url.searchParams.set('name', fullUrl);
    url.searchParams.set('ttl', ttlSeconds.toString());

    // Make request to tiny.amazon.com API
    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        Accept: 'application/json', // Request JSON response
      },
      credentials: 'include', // Include cookies for authentication
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Failed to create tiny URL: ${response.status} ${response.statusText}. ${errorText}`,
      );
    }

    // Parse JSON response
    const data = await response.json();

    if (!data.short_url) {
      throw new Error('Tiny URL service did not return a valid short URL');
    }

    return {
      tinyUrl: data.short_url,
      fullUrl,
      expiresAt,
    };
  } catch (error) {
    // If tiny URL creation fails, provide helpful error message
    if (error instanceof Error) {
      throw new Error(`Tiny URL creation failed: ${error.message}`);
    }
    throw new Error('Tiny URL creation failed: Unknown error');
  }
}
